import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import UserHeader from "../UserHeader";
import ListingHeader from "../ListingHeader";
import { useStyles } from "./style";
import { Box } from "@material-ui/core";
import { Helmet } from "react-helmet";
import {
  useLayoutToggle,
  useRedesignTabs,
} from "../../RedesignLayout/LayoutToggleContext";
import { validateBoolItem } from "../../../utils/helpers";

const ListingLayout = ({
  item,
  title,
  redirect,
  // Content
  children,
  // Button
  hasFilter,
  hasDateFilter,
  hasSorting,
  hasAddTitle,
  addTitle,
  // Button Icon
  hideAdd,
  // Button Actions
  onFilterAction,
  onDateFilter,
  onFilterSorting,
  // Location Toggler
  showLocationToggle,
  // Add Modal
  isAddModal,
  onAddModal,
  //
  paths,
  showProfileTabs,
  profileLocation,
  //
  hasListFilter,
  listfilter,
  onListFilterClick,
  activeListFilter,
  stats,
  hasCount,
  hasCardActions,
  onCardAction,
  cardActionTitle,
  hideOrder,
  showInfoText,
  infoText,
  hasLargeContent,
  hasMediumContent,
  hasFullContent,
  hasYear,
  year,
  onYear,
  yearList,
  hasViewToggle,
  showTimeline,
  onViewChange,
  hasTaskToggle,
  onTaskToggle,
  taskToggle,
  hideHeader,
  hasCsvmodal,
  onCsvAction,
  hasAiImport,
  onAiImportAction,
  hasImportSurvey,
  onImportSurveyAction,
  importSurveyTitle,
  hasImportedSurveys,
  onImportedSurveysAction,
  importedSurveysTitle,
  onLocationChange,
  customContent,
  taskToggleLabel,
  showSearch,
  actionContent,
  showWelcomeMessage,
  isDashboard,
  customTitleContent,
  showHeaderWithProfileTabs,
  zenBackdrop,
  headerLayout,
}) => {
  const classes = useStyles();
  const { useRedesign } = useLayoutToggle();
  const { setPageTabs, clearPageTabs, setShowLocationSwitcher } =
    useRedesignTabs();
  const userInfo = useSelector((state) => state.AuthReducer.user);

  // Mockup puts page tabs in the TopBar — register them from ListingLayout.
  useEffect(() => {
    if (!useRedesign || !showProfileTabs || !paths?.length) {
      if (useRedesign) clearPageTabs();
      return undefined;
    }

    const nextTabs = paths
      .filter((path) => {
        if (path?.requires_check) {
          return validateBoolItem(path, userInfo, userInfo?.accounting_type);
        }
        return true;
      })
      .map((path) => ({
        title: path.title,
        to: path.path_to_go || path.path,
        match: path.path_to_match || path.path,
        indicators: path.indicators,
      }));

    setPageTabs(nextTabs);
    return () => clearPageTabs();
  }, [
    useRedesign,
    showProfileTabs,
    paths,
    userInfo,
    setPageTabs,
    clearPageTabs,
  ]);

  // Match old layout: location switcher only when the page opts in.
  useEffect(() => {
    if (!useRedesign) return undefined;
    setShowLocationSwitcher(Boolean(showLocationToggle));
    return () => setShowLocationSwitcher(false);
  }, [useRedesign, showLocationToggle, setShowLocationSwitcher]);

  const getContentClasses = () => {
    let renderedClasses = `${classes.content}`;
    if (hasMediumContent) renderedClasses += ` ${classes.contentMD}`;
    if (hasLargeContent) renderedClasses += ` ${classes.contentLG}`;
    if (hasFullContent) renderedClasses += ` ${classes.contentFull}`;
    // Redesign shell already pads `.redesign-content-area` — avoid stacking
    // another 15px inset (empty band around every listing page).
    if (useRedesign) renderedClasses += ` ${classes.contentRedesign}`;
    return renderedClasses;
  };

  // The top bar sits in a sibling of the content box, so it has to be told the
  // same width variant — otherwise it spans the full screen while the listing
  // under it is capped, and their edges never align on a wide monitor.
  const getHeaderInnerClasses = () => {
    let renderedClasses = `${classes.headerInner}`;
    if (hasMediumContent) renderedClasses += ` ${classes.contentMD}`;
    if (hasLargeContent) renderedClasses += ` ${classes.contentLG}`;
    if (hasFullContent) renderedClasses += ` ${classes.contentFull}`;
    return renderedClasses;
  };

  const getContentWrapperClasses = () => {
    let renderedClasses = `${classes.contentWrapper || ""}`.trim();
    // Redesign uses mockup cream (#f6f5f2) from the layout shell — skip the
    // cool dashboard tint/gradients so the right content section matches.
    if (isDashboard && !useRedesign)
      renderedClasses += ` ${classes.contentDashboard}`;
    if (zenBackdrop) renderedClasses += ` ${classes.contentZen}`;
    if (useRedesign && zenBackdrop)
      renderedClasses += ` ${classes.contentZenRedesign}`;
    return renderedClasses.trim();
  };

  // Redesign TopBar owns chrome — skip ListingLayout header (Overview's
  // showWelcomeMessage was leaving an empty tall strip under the top bar).
  const showHeaderChrome = !useRedesign || showSearch;

  // TopBar already shows the page title. Hide ListingHeader unless it has
  // real actions/filters — otherwise we get an empty padded strip (esp. when
  // title is "" / hideAdd on Overview).
  const hasListingHeaderUtility =
    hideAdd !== true ||
    Boolean(customContent) ||
    Boolean(actionContent) ||
    Boolean(customTitleContent) ||
    Boolean(hasFilter) ||
    Boolean(hasListFilter) ||
    Boolean(hasYear) ||
    Boolean(hasViewToggle) ||
    Boolean(hasTaskToggle) ||
    Boolean(hasCsvmodal) ||
    Boolean(hasImportSurvey) ||
    Boolean(hasImportedSurveys) ||
    Boolean(hasCardActions) ||
    Boolean(showInfoText) ||
    Boolean(showTimeline) ||
    Boolean(showHeaderWithProfileTabs);

  const effectiveHideHeader = hideHeader === true || !hasListingHeaderUtility;

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      {showHeaderChrome && (
        <Box className={useRedesign ? classes.headerRedesign : classes.header}>
          <Box className={useRedesign ? undefined : getHeaderInnerClasses()}>
            <UserHeader
              title={title}
              showLocationToggle={showLocationToggle}
              profileTabs={paths}
              showProfileTabs={showProfileTabs}
              profileLocation={profileLocation}
              onLocationChange={onLocationChange}
              showSearch={showSearch}
              showWelcomeMessage={showWelcomeMessage}
            />
          </Box>
        </Box>
      )}
      <Box className={getContentWrapperClasses()}>
        <Box className={getContentClasses()}>
          <ListingHeader
            title={title}
            hideTitle={!showProfileTabs && !showWelcomeMessage}
            customTitleContent={customTitleContent}
            redirect={redirect}
            item={item}
            hasFilter={hasFilter}
            hasDateFilter={hasDateFilter}
            hasSorting={hasSorting}
            onFilterAction={onFilterAction}
            onFilterSorting={onFilterSorting}
            onDateFilter={onDateFilter}
            hideAdd={hideAdd}
            isAddModal={isAddModal}
            onAddModal={onAddModal}
            hasAddTitle={hasAddTitle}
            addTitle={addTitle}
            hasListFilter={hasListFilter}
            listfilter={listfilter}
            onListFilterClick={onListFilterClick}
            activeListFilter={activeListFilter}
            stats={stats}
            hasCount={hasCount}
            hasCardActions={hasCardActions}
            onCardAction={onCardAction}
            cardActionTitle={cardActionTitle}
            hideOrder={hideOrder}
            showInfoText={showInfoText}
            infoText={infoText}
            hasYear={hasYear}
            year={year}
            onYear={onYear}
            yearList={yearList}
            hasViewToggle={hasViewToggle}
            showTimeline={showTimeline}
            onViewChange={onViewChange}
            showProfileTabs={showProfileTabs}
            hasTaskToggle={hasTaskToggle}
            onTaskToggle={onTaskToggle}
            taskToggle={taskToggle}
            hideHeader={effectiveHideHeader}
            hasCsvmodal={hasCsvmodal}
            onCsvAction={onCsvAction}
            hasAiImport={hasAiImport}
            onAiImportAction={onAiImportAction}
            hasImportSurvey={hasImportSurvey}
            onImportSurveyAction={onImportSurveyAction}
            importSurveyTitle={importSurveyTitle}
            hasImportedSurveys={hasImportedSurveys}
            onImportedSurveysAction={onImportedSurveysAction}
            importedSurveysTitle={importedSurveysTitle}
            customContent={customContent}
            taskToggleLabel={taskToggleLabel}
            actionContent={actionContent}
            showHeaderWithProfileTabs={showHeaderWithProfileTabs}
            headerLayout={headerLayout}
          />
          {children}
        </Box>
      </Box>
    </>
  );
};

export default ListingLayout;
